/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { getPayloadObject, getTelemetryAttributes, getTelemetryObject, IModuleProps, INodeProps, IPayLoad, ITelemetryContent, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import classnames from 'classnames';

import { IMfrmSocialIconsProps } from './mfrm-social-icons.props.autogenerated';
import { IMfrmSocialIconsData } from './mfrm-social-icons.data';

export interface IMfrmSocialIconsViewProps extends IMfrmSocialIconsProps<IMfrmSocialIconsData> {
    iconText: React.ReactNode;
    image?: React.ReactNode;
    Link: INodeProps;
    SocialIcon: IModuleProps;
}

/**
 *
 * MfrmSocialIcons component
 * @extends {React.PureComponent<IMfrmSocialIconsProps<{}}>>}
 */
class MfrmSocialIcons extends React.PureComponent<IMfrmSocialIconsProps<IMfrmSocialIconsData>> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    private attributes: Msdyn365.IDictionary<string> | undefined;

    constructor(props: IMfrmSocialIconsProps<IMfrmSocialIconsData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry);

        // Construct telemetry attribute to render
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    public render(): JSX.Element | null {
        const {
            text,
            image,
            link,
            className
        } = this.props.config;

        const iconText = text ? text : '';
        this.payLoad.contentAction.etext = iconText;
        this.attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);

        const imageProps = {
            gridSettings: this.props.context.request.gridSettings || {},
            imageSettings: image && image.imageSettings
        };

        const iconImage = image && (
        <Msdyn365.Image {...image} {...imageProps} editProps={{ key: this.props.config.image || {}, requestContext: this.props.context.request }} />
        );

        if (
            !iconText &&
            !iconImage
          ) {
            this.props.context.telemetry.warning(
              'MFRM Social Icons content is empty, module wont render.'
            );
            return null;
          }

          const MfrmSocialIconsViewProps = {
              ...this.props,
              iconText: this._createText(text!),
              image: iconImage,
              SocialIcon: {
                moduleProps: this.props,
                className: classnames('mfrm-social', className)
              },
              Link: link && link.linkUrl && link.linkUrl.destinationUrl && {
                tag: 'a',
                role: 'link',
                className: 'mfrm-social__link',
                href: link.linkUrl.destinationUrl,
                title: iconText,
                target: link.openInNewTab ? '_blank' : '_self',
                onClick: onTelemetryClick(this.telemetryContent, this.payLoad, iconText),
                ...this.attributes
              }
         };

        return this.props.renderView(MfrmSocialIconsViewProps) as React.ReactElement;
    }

    public handleTextChange = (event: Msdyn365.ContentEditableEvent) => this.props.config.text = event.target.value;

    private _createText(text: string): React.ReactNode | null {
        if (!text) {
            return null;
        }
        return (
            <Msdyn365.Text
                text={text}
                className='mfrm-social__text'
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
          />
        );
    }
}

export default MfrmSocialIcons;
